import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";
import { LEGAL_VERSION, LEGAL_EFFECTIVE_DATE } from "../lib/legal";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal that appears automatically after login when a returning user's
 * accepted terms_version is older than the current LEGAL_VERSION. The user
 * must acknowledge before continuing; there is no dismiss-forever button.
 *
 * Legacy accounts (created before terms_version tracking) have terms_version
 * unset — we treat that as "must accept" too, matching the Privacy Policy
 * promise that material changes trigger a re-prompt.
 */
export default function TermsReconsentModal() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!user) { setOpen(false); return; }
    // Only prompt users who have already accepted at least once — new signups
    // just accepted the current version so `terms_version` is populated.
    // Legacy users with `terms_accepted=false` are handled by OnboardingDialog.
    if (user.terms_accepted !== true) { setOpen(false); return; }
    const current = user.terms_version || null;
    if (current !== LEGAL_VERSION) {
      setOpen(true);
      setAgreed(false);
    } else {
      setOpen(false);
    }
  }, [user?.terms_version, user?.terms_accepted, user?.id]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      await api.post("/auth/accept-terms", { version: LEGAL_VERSION });
      await refreshUser();
      toast.success("Thanks — updated policy accepted");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={() => { /* not dismissable — user must accept */ }}>
      <DialogContent
        className="sm:max-w-lg bg-white border-slate-200 rounded-none [&>button[aria-label='Close']]:hidden [&>button.absolute.right-4.top-4]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="terms-reconsent-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-teal-50" style={{ color: "#0D9488" }}>
              <ShieldCheck className="w-4 h-4" strokeWidth={2.2} />
            </div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-teal-700">
              Policy update · v{LEGAL_VERSION}
            </div>
          </div>
          <DialogTitle className="text-slate-900 text-2xl">We've updated our Privacy Policy and Terms of Service</DialogTitle>
          <DialogDescription className="text-slate-600 mt-2">
            Effective {LEGAL_EFFECTIVE_DATE}. Please review the changes and confirm to continue using {" "}
            <span className="font-semibold">The Amino Exchange</span>. We're only asking because the version
            you last agreed to is out of date — your data has not changed.
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-900 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            <b>Reminder:</b> {" "}
            The Amino Exchange is a tracking tool. It does not provide medical advice —
            always consult a licensed professional before starting or changing any protocol.
          </span>
        </div>

        <label
          className="flex items-start gap-3 cursor-pointer select-none rounded-lg border px-3 py-2.5 transition-colors"
          style={{
            borderColor: agreed ? "#0D9488" : "#E2E8F0",
            background: agreed ? "rgba(13,148,136,0.05)" : "#FFFFFF",
          }}
          data-testid="reconsent-agree-row"
        >
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 cursor-pointer accent-[#0D9488]"
            data-testid="reconsent-checkbox"
          />
          <span className="text-sm leading-snug text-slate-800">
            I've reviewed the updated{" "}
            <Link to="/privacy" target="_blank" rel="noopener" className="font-semibold underline text-teal-700" data-testid="reconsent-privacy-link">Privacy Policy</Link>
            {" "}and{" "}
            <Link to="/terms" target="_blank" rel="noopener" className="font-semibold underline text-teal-700" data-testid="reconsent-terms-link">Terms of Service</Link>
            {" "}and I agree.
          </span>
        </label>

        <DialogFooter className="mt-2">
          <Button
            onClick={confirm}
            disabled={!agreed || submitting}
            className="bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-none uppercase tracking-widest text-xs font-semibold h-11 px-6 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="reconsent-confirm"
          >
            {submitting ? "Saving…" : "Accept & continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
